<template>
  <MonitorWrapper class="cr0534-view">
    <div class="common-border">
      <div class="cr0534-view-header">
        <ModsIcon :showText="false" width="120px" icon="icon-he" :wsData="wsData" :mods="curMod">
          <span class="icon-text">{{ state == 0 ? $t('正常') : alarmText }}</span>
        </ModsIcon>
        <div class="header-data">
          <div class="header-item">
            <span class="label">{{ $t('剂量率') }}</span>
            <span class="value">{{ radDose }}</span>
            <span class="unit">μSv</span>
          </div>
          <div class="header-item">
            <span class="label">{{ $t('报警状态') }}</span>
            <span class="value" :class="getStateClass(state)">
              {{ $t(getStateText(state)) }}
            </span>
          </div>
        </div>
      </div>
      <div class="cr0534-view-content">
        <v-chart :options="relativeValOp" :autoresize="true" class="chart" />
      </div>
    </div>
  </MonitorWrapper>
</template>

<script>
import { formatTimestamp } from '@/utils/utils';
import { lineAreaStyle } from '@/echartOption/lineAreaStyle';
import { graColor } from '@/echartOption/common';
import monitorMixin from '@/mixins/monitorMixin';
import ModsIcon from '@/components/mods-icon';

export default {
  name: 'CR0534',
  mixins: [monitorMixin],
  components: { ModsIcon },
  data() {
    return {
      maxLen: 30,
      radDose: '',
      state: 0,
      state2: 0,
      wsData: null,
      curMod: 'nuclearMod',
      alarmText: '',
      relativeValOp: lineAreaStyle({
        color: ['#833BBB', '#3368F2'],
        legend: {
          top: 'auto',
          right: '5%',
          icon: 'circle',
          orient: 'vertical',
          textStyle: {
            color: '#fff'
          }
        },
        title: { text: this.$t('剂量率/μSv'), left: 'center' },
        yAxis: { type: 'value' },
        xAxis: {
          type: 'category',
          data: []
        },
        series: [
          {
            name: '',
            type: 'line',
            symbol: 'emptyCircle',
            areaStyle: { color: graColor('rgb(3, 153, 224)', 'rgba(3, 153, 224, 0.1)') },
            smooth: true,
            data: []
          }
        ]
      })
    };
  },
  methods: {
    langChangeHandler() {
      this.relativeValOp.title.text = this.$t('剂量率/μSv');
    },
    getStateClass(state) {
      const classMap = {
        0: 'normal-color',
        1: 'warning-color',
        2: 'danger-color'
      };
      return classMap[state] || 'normal-color';
    },
    updateData(res) {
      this.wsData = res;
      this.setData(res);
    },
    setData(res) {
      let { nuclearMod } = res.mods;

      let { rg04Dto, radDose } = nuclearMod;

      this.radDose = radDose;
      this.state = res.state;
      this.state2 = res.state2;

      this.alarmText = nuclearMod.alarmContent;

      this.relativeValOp.xAxis.data.push(formatTimestamp(res.beginTime));
      this.relativeValOp.series[0].data.push(rg04Dto.radDose);

      if (this.relativeValOp.xAxis.data.length > this.maxLen) {
        this.relativeValOp.xAxis.data.shift();
        this.relativeValOp.series[0].data.shift();
      }
    }
  }
};
</script>

<style lang="scss">
.cr0534-view {
  width: 100%;
  height: 100%;
  padding: 10px;
  box-sizing: border-box;

  span {
    @include state-textstyle;
  }

  .common-border {
    height: 100%;
    padding: 2px;
    background-image: url('~@/assets/image/lt.png'), url('~@/assets/image/rt.png'),
      url('~@/assets/image/lb.png'), url('~@/assets/image/rb.png'),
      url('~@/assets/image/linelight.png');
    background-repeat: no-repeat, no-repeat, no-repeat, no-repeat, no-repeat;
    background-position: top left, top right, bottom left, bottom right, center -5px;
    display: flex;
    flex-direction: column;

    .cr0534-view-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 20px;
      background: linear-gradient(135deg, rgba(1, 60, 93, 0.8), rgba(1, 40, 65, 0.6));
      border-radius: 6px;
      margin: 8px;
      border: 1px solid rgba(0, 238, 255, 0.15);
      flex-shrink: 0;

      .header-data {
        display: flex;
        align-items: center;
        gap: 40px;
      }

      .header-item {
        display: flex;
        align-items: baseline;
        font-size: 16px;

        .label {
          margin-right: 10px;
          color: rgba(204, 204, 204, 0.8);
          font-size: 14px;
        }

        .value {
          font-weight: bold;
          margin-right: 5px;
          font-size: 20px;
          color: #00eeff;
          text-shadow: 0 0 10px rgba(0, 238, 255, 0.3);

          &.danger-color {
            color: #ff4847;
            text-shadow: 0 0 10px rgba(255, 72, 71, 0.3);
          }

          &.warning-color {
            color: #ff8f17;
            text-shadow: 0 0 10px rgba(255, 143, 23, 0.3);
          }
        }

        .unit {
          font-size: 13px;
          color: rgba(170, 170, 170, 0.8);
        }
      }
    }

    .cr0534-view-content {
      flex: 1;
      padding: 0 8px 8px;
      min-height: 0;

      .chart {
        height: 100% !important;
        border: 1px solid rgba(96, 118, 173, 0.6);
        border-radius: 6px;
        background: rgba(0, 20, 40, 0.3);
      }
    }
  }

  // 平板适配 (768px - 1024px)
  @media screen and (max-width: 1024px) {
    padding: 5px;

    .common-border {
      .cr0534-view-header {
        padding: 8px 15px;
        margin: 5px;

        .header-data {
          gap: 25px;
        }

        .header-item {
          font-size: 14px;

          .label {
            margin-right: 8px;
            font-size: 12px;
          }

          .value {
            font-size: 16px;
          }

          .unit {
            font-size: 11px;
          }
        }
      }

      .cr0534-view-content {
        padding: 0 5px 5px;
      }
    }
  }

  // 1920平板适配
  @media screen and (width: 1920px) {
    padding: 8px;

    .common-border {
      .cr0534-view-header {
        padding: 10px 18px;

        .header-data {
          gap: 35px;
        }

        .header-item {
          .label {
            font-size: 13px;
          }

          .value {
            font-size: 18px;
          }

          .unit {
            font-size: 12px;
          }
        }
      }
    }
  }
}
</style>
